<template>
  <b-modal
    @shown="shown"
    @hidden="hidden"
    id="modal-customer-lock"
    ref="modal-customer-lock"
    scrollable
    footer-class="justify-content-start"
    size="xl"
  >
    <template v-slot:modal-title>
      <h4 class="p-3 mb-0 font-weight-boldy">Select customer</h4>
      <R-Label
        class="px-3"
        htmlFor="modal-header"
        value="Select the customer you want to work on"
      />
    </template>
    <b-container fluid>
      <b-row class="pb-4">
        <b-col>
          <b-list-group
            horizontal
            v-if="getCustomer.customerName"
            class="align-items-center"
          >
            <b-list-group-item class="d-flex align-items-center">
              <span class="font-weight-bold mr-auto">Customer:</span>
              <span class="ml-2 mr-auto">{{ getCustomer.customerName }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex align-items-center">
              <span class="font-weight-bold ml-2 mr-auto"> Id:</span>
              <span class="ml-2 mr-auto">{{ getCustomer.cid }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex align-items-center">
              <span class="font-weight-bold mr-auto">Admin:</span>
              <span class="ml-2 mr-auto">{{ getCustomer.lid }}</span>
            </b-list-group-item>
            <b-list-group-item class="d-flex align-items-center">
              <span class="font-weight-bold ml-2 mr-auto">Pbx:</span>
              <span class="ml-2" v-if="getCustomer.pbx">{{
                getCustomer.pbx.name
              }}</span>
            </b-list-group-item>
            <R-Button
              class="ml-4"
              :size="'small'"
              @onClick="clearSelection()"
              :variant="'secondary'"
              >Clear</R-Button
            >
          </b-list-group>
          <b-alert class="mb-0" variant="warning" v-else show>
            <span class="font-weight-bold mr-auto">Customer:</span>
            <span class="ml-2 mr-auto">No customer selected!</span>
          </b-alert>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col sm="3">
          <R-Select
            :labelValue="'Field:'"
            placeholder="Search field"
            :isClearable="false"
            :value="searchField"
            :options="sSearchFieldOptions"
            ref="searchField"
            @onChange="
              $event
                ? (searchField = { label: $event.label, value: $event.value })
                : (searchField = null);
              customerSearch();
            "
          ></R-Select>
        </b-col>
        <b-col>
          <R-Label :theme="labelTheme" htmlFor="search" value="Search:" />
          <b-form-input
            class="w-100 mt-1"
            @keyup.enter="customerSearch()"
            v-model="search"
            ref="searchField"
            placeholder="Value to search"
          ></b-form-input>

          <div
            class="search-icon cursor-pointer rounded mt-1 mr-1"
            @click="customerSearch()"
          >
            <SearchIcon :color="'#fff'"></SearchIcon>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col class="pb-3">
          <b-table
            show-empty
            outlined
            small
            responsive
            selectable
            hover
            head-variant="light"
            ref="customersTable"
            select-mode="single"
            no-select-on-click
            :busy="isSearching"
            :current-page="currentPage"
            :per-page="perPage"
            :fields="tableFields"
            :items="displayList"
          >
            <template
              v-slot:cell(adminLoginList)="data"
              v-on:click.stop=""
              @click.native.stop
            >
              <ValidationProvider
                v-if="data.item.adminLoginList.length > 1"
                :ref="'admin' + data.item.customerId"
                rules="required"
                immediate
                v-slot="{ errors }"
              >
                <b-form-select
                  :class="[{ 'border-danger': errors[0] }]"
                  v-model="data.item.selectedAdmin"
                  :options="data.item.adminLoginList"
                >
                  <template v-slot:first>
                    <b-form-select-option value="null" disabled
                      >Select option</b-form-select-option
                    >
                  </template>
                </b-form-select>
                <div class="text-danger">{{ errors[0] }}</div>
              </ValidationProvider>
              <div
                v-if="data.item.adminLoginList.length === 1"
                class="d-inline-flex align-items-center"
                style="min-height: 40px"
              >
                {{ data.item.selectedAdmin }}
              </div>
            </template>

            <template v-slot:cell(pbxNameList)="data">
              <ValidationProvider
                v-if="data.item.pbxList.length > 1"
                :ref="'pbxname' + data.item.customerId"
                :rules="requirePbx ? 'required' : null"
                immediate
                v-slot="{ errors }"
              >
                <b-form-select
                  :class="[{ 'border-danger': errors[0] }]"
                  text-field="name"
                  v-model="data.item.selectedPbx"
                  :options="data.item.pbxList"
                  @change="$forceUpdate()"
                >
                  <template v-slot:first>
                    <b-form-select-option value="null" disabled
                      >Select option</b-form-select-option
                    >
                  </template>
                </b-form-select>
                <div class="text-danger">{{ errors[0] }}</div>
              </ValidationProvider>
              <div
                v-if="data.item.pbxList.length === 1"
                class="d-inline-flex align-items-center"
                style="min-height: 40px"
              >
                {{ data.item.selectedPbx.name }}
              </div>
            </template>

            <template v-slot:cell(pbxIdList)="data">
              <ValidationProvider
                v-if="data.item.pbxList.length > 1"
                :ref="'pbxid' + data.item.customerId"
                :rules="requirePbx ? 'required' : null"
                immediate
                v-slot="{ errors }"
              >
                <b-form-select
                  :class="[{ 'border-danger': errors[0] }]"
                  text-field="id"
                  v-model="data.item.selectedPbx"
                  :options="data.item.pbxList"
                  @change="$forceUpdate()"
                >
                  <template v-slot:first>
                    <b-form-select-option value="null" disabled
                      >Select option</b-form-select-option
                    >
                  </template>
                </b-form-select>
                <div class="text-danger">{{ errors[0] }}</div>
              </ValidationProvider>
              <div
                v-if="data.item.pbxList.length === 1"
                class="d-inline-flex align-items-center"
                style="min-height: 40px"
              >
                {{ data.item.selectedPbx.id }}
              </div>
            </template>

            <!-- Select row checkbox -->
            <template v-slot:cell(select)="row">
              <b-form-checkbox
                :disabled="isRowDisabled(row.item.customerId)"
                :checked="row.rowSelected"
                @change="selectRow(row)"
              ></b-form-checkbox>
            </template>

            <template v-slot:table-busy>
              <b-spinner
                class="spinner-lg mt-3"
                variant="primary"
                label="Loading"
              ></b-spinner>
              <R-Label
                className="justify-content-center mt-2"
                :theme="labelSpinner"
                :value="'Loading...'"
              />
            </template>

            <template v-slot:empty="scope">
              <h4 class="p-3">{{ scope.emptyText }}</h4>
            </template>
            <template v-slot:emptyfiltered="scope">
              <h4 class="p-3">{{ scope.emptyFilteredText }}</h4>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </b-container>

    <template v-slot:modal-footer="{ cancel }">
      <R-Button
        class="ml-3"
        :disabled="!selected.customerName || isSearching || isFetchingToken"
        @onClick="lockCustomer()"
        >Confirm</R-Button
      >
      <R-Button @onClick="cancel()" :variant="'secondary'">Cancel</R-Button>
    </template>
  </b-modal>
</template>

<script>
import axios from "axios";

import { mapActions, mapGetters } from "vuex";
import { DURATION, notify, TYPE } from "@8x8/oxygen-toaster";
import { extend, ValidationProvider } from "vee-validate";
import { required } from "vee-validate/dist/rules";
import debounce from "lodash/debounce";
import { PILOT, PRODUCTION } from "@/vue/utils/helper";
import { getEnv } from '@/env';

const env = getEnv('VUE_APP_NODE_ENV');

extend("required", {
  ...required,
  message: "This field is required",
});
const CUSTOMER_SEARCH_DEBOUNCE_TIME = 50;
export default {
  components: {
    ValidationProvider,
  },
  props: {
    requireCustomerToken: Boolean,
    requirePbx: Boolean,
  },
  data() {
    return {
      selected: {},
      labelTheme: { fontWeight: 600 },
      labelTitle: { fontSize: "24px", fontWeight: 600 },
      labelSpinner: { fontColor: "#0376da", fontWeight: 600 },
      search: "",
      cancelSource: null,
      isSearching: false,
      isFetchingToken: false,
      skipToken: false,
      searchField: {
        value: "customerId",
        label: "Customer Id",
      },
      sSearchFieldOptions: [
        {
          value: "customerId",
          label: "Customer Id",
        },
        {
          value: "customerName",
          label: "Customer Name",
        },
        {
          value: "pbxName",
          label: "Pbx Name",
        },
        {
          value: "tenantName",
          label: "Tenant Name",
        },
      ],

      // table
      tableFields: [
        { key: "select", class: "text-center align-middle py-2" },
        { key: "customerName", class: "w-auto align-middle" },
        { key: "customerId", class: "w-auto align-middle" },
        {
          key: "adminLoginList",
          label: "Admin Login",
          class: "w-auto align-middle",
        },
        { key: "pbxNameList", label: "Pbx Name", class: "w-auto align-middle" },
        { key: "pbxIdList", label: "Pbx Id", class: "w-auto align-middle" },
      ],
      perPage: 25,
      totalRows: 1,
      currentPage: 1,
      emptyText: "No patents",
      emptyFilteredText: "No Search Results",
      customers: [],
    };
  },
  computed: {
    isPilot() {
      return env !== "production";
    },
    ...mapGetters(["getCustomer"]),
    displayList() {
      return this._.map(this.customers, (value) => {
        //used for bootstrap select
        value["pbxList"] = this._.map(value["pbxList"], function (el) {
          return { value: el, name: el.name, id: el.id };
        });

        value.selectedAdmin =
          value["adminLoginList"].length > 0 ? value.adminLoginList[0] : null;
        value.selectedPbx =
          value["pbxList"].length > 0 && value["pbxList"][0]
            ? value["pbxList"][0].value
            : null;

        return value;
      });
    },
  },
  mounted() {
    EventBus.$on("skipCustomerToken", () => {
      this.skipToken = true;
    });
  },
  methods: {
    ...mapActions(["fetchCustomer", "clearCustomer"]),
    isRowDisabled(id) {
      this.$nextTick().then(() => {
        // DOM updated
        if (
          this.$refs["admin" + id] &&
          this.$refs["pbxname" + id] &&
          this.$refs["pbxid" + id]
        ) {
          let invalidAdmin = this.$refs["admin" + id].errors.length > 0;
          let invalidPbx = false;
          if (this.requirePbx) {
            invalidPbx =
              this.$refs["pbxname" + id].errors.length > 0 ||
              this.$refs["pbxid" + id].errors.length > 0;
          }
          return invalidAdmin || invalidPbx;
        } else {
          return true;
        }
      });
    },
    async lockCustomer() {
      if (!this.selected) {
        return;
      }
      this.isFetchingToken = true;
      console.log("lockCustomer");
      let lockingToast = notify({
        content: "Selecting customer...",
        duration: DURATION.INFINITE,
      });
      //allow customer selection without token retrieval
      this.fetchCustomer({
        customerId: this.selected.customerId,
        pbx: this.selected.selectedPbx,
        pbxList: this.selected.pbxList,
        adminList: this.selected.adminLoginList,
        customerName: this.selected.customerName,
        tenantInfo: this.selected.tenantInfo,
        success: (response) => {
          lockingToast.close();
          console.log("got lockCustomer");
          console.log(response);
          notify({ content: response, duration: 5000 });
          this.isFetchingToken = false;
          EventBus.$emit("lock-customer");
          this.$refs["modal-customer-lock"].hide();
        },
        error: (response) => {
          lockingToast.close();
          console.log("got error lockCustomer");
          console.log(response);
          this.isFetchingToken = false;
          notify({
            content: response,
            type: TYPE.error,
            duration: DURATION.INFINITE,
          });
        },
      });
    },
    selectRow(row) {
      console.log("selectRow");
      console.log(row);
      if (row.rowSelected) {
        this.selected = {};
        this.$refs.customersTable.unselectRow(row.index);
      } else {
        this.selected = row.item;
        this.selected["index"] = row.index;
        this.$refs.customersTable.selectRow(row.index);
      }
    },
    shown() {
      if (this.selected.customerName) {
        this.$refs.customersTable.selectRow(this.selected.index);
      }

      if (this.$refs.searchField) {
        this.$refs.searchField.$el.focus();
      }
    },
    hidden() {
      this.skipToken = false;
    },
    customerSearch: debounce(async function () {
      if (!this.searchField || !this.search) {
        return;
      }

      console.log("searching: " + this.search);
      this.customers = [];
      this.selected = {};

      this.cancelSearch();
      this.cancelSource = axios.CancelToken.source();
      this.isSearching = true;

      try {
        this.customers = await this.$store.dispatch("searchCustomers", {
          platform: this.isPilot ? PILOT : PRODUCTION,
          searchField: this.searchField.value,
          searchTerm: this.search,
          config: {
            cancelToken: this.cancelSource.token,
          },
        });
        this.cancelSource = null;
        this.isSearching = false;
        this.$forceUpdate();
      } catch (error) {
        this.isSearching = error.message === "canceled";
      }
    }, CUSTOMER_SEARCH_DEBOUNCE_TIME),
    cancelSearch() {
      if (this.cancelSource) {
        this.cancelSource.cancel("canceled");
      }
    },
    clearSelection() {
      this.clearCustomer();
      this.selected = {};
      this.$refs["modal-customer-lock"].hide();
      EventBus.$emit("clear-customer");
    },
  },
};
</script>
